import { useTranslation } from 'react-i18next';
import { Badge, Tooltip } from '@heroui/react';
import { BellOffIcon } from '@/components/icons';
import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useLongPress } from '@/lib/useLongPress';
import type { GuildAvatarDragHandlers } from './railReorder';
import { railUnreadLabel } from './folderRules';
import { voiceMarkerLabel } from './railVoice';
import { TooltipTarget } from '@/components/ui/TooltipTarget';

/** One server avatar in the rail — active halo, unread/muted badge, drag
 *  handlers + long-press context menu. Extracted from HeroTopBar. */
export function GuildAvatarButton(props: {
  guild: { id: string; name: string; iconUrl?: string | null };
  hue: { bg: string; text: string };
  unread: number;
  /** People sitting in this server's voice channels right now. */
  voiceCount?: number;
  active: boolean;
  muted: boolean;
  isOver: boolean;
  dragOverPos: 'before' | 'after' | 'merge' | null;
  isDragging: boolean;
  dragHandlers: GuildAvatarDragHandlers;
  onSelect: () => void;
  onOpenMenu: (x: number, y: number) => void;
  /** Vertical (cispracs) rail — moves the active indicator to the left edge. */
  vertical?: boolean;
  /** Suppress the built-in active halo/tab — used when the active marker is
   *  drawn externally (the vertical rail's left pill for top-level servers). */
  hideActiveIndicator?: boolean;
}) {
  const { t } = useTranslation();
  const { guild: g, unread, active, muted, isOver, dragOverPos, isDragging } = props;
  const voiceLabel = voiceMarkerLabel(props.voiceCount ?? 0);
  const tooltip = [g.name, unread > 0 ? t('ui.topbar.unreadCount', { count: unread }) : null]
    .filter(Boolean)
    .join(' · ');
  const longPress = useLongPress(props.onOpenMenu);
  return (
    <Tooltip delay={0}>
      <TooltipTarget>
        <button
          aria-label={muted ? t('ui.topbar.serverMutedLabel', { name: g.name }) : g.name}
          type="button"
          onClick={props.onSelect}
          onContextMenu={(e) => {
            e.preventDefault();
            props.onOpenMenu(e.clientX, e.clientY);
          }}
          {...props.dragHandlers}
          {...longPress}
          className={[
        // `flex` (block-level), NOT inline-flex: a server with an image
        // icon renders HeroUI's <Avatar.Image> as an absolutely positioned
        // element, so an inline-flex button has no text baseline and the
        // line box adds descender space.
        // Rail controls share the same 6px corner radius.
        'studio-guild-control relative flex shrink-0 select-none rounded-[6px] outline-none transition-[transform,opacity] [-webkit-touch-callout:none] hover:scale-105',
        // Active servers lean a couple of pixels toward the content they open.

        muted && !active ? 'opacity-60 hover:opacity-100' : '',
        isDragging ? 'opacity-40 scale-95' : '',
        // Drop-target indicator: a 2px accent-coloured left or right border.
        isOver && dragOverPos === 'before' ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface' : '',
        isOver && dragOverPos === 'after' ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface' : '',
        // Merge intent (drop on centre → make/join a folder): a distinct
        // green halo + slight grow so it reads as "combine", not "reorder".
        isOver && dragOverPos === 'merge' ? 'ring-2 ring-success ring-offset-2 ring-offset-surface scale-110' : '',
      ].filter(Boolean).join(' ')}
    >
      {active && !props.hideActiveIndicator && <span aria-hidden="true" className={props.vertical ? "studio-guild-active-marker absolute -left-2 top-4 h-4 w-1 rounded-full bg-accent" : "studio-guild-active-marker absolute -bottom-2 left-4 h-1 w-4 rounded-full bg-accent"} />}
      <span className="rail-badges relative z-10 flex">
        <Badge.Anchor>
          {/* overflow-hidden + the radius on BOTH the root and the fallback: the
              uploaded icon renders as an absolutely positioned <img>, so the
              root's radius alone would leave square photo corners. */}
          <span className="grid size-12 place-items-center"><IdentityAvatar id={g.id} name={g.name} src={g.iconUrl} size="md" className="studio-guild-avatar" /></span>
          {/* How many people are sitting in this server's voice right now.
              Success-green, not the accent: unread is a queue to catch up on,
              this is a room that is live, and the two must not read as the same
              signal. Bottom-right so it never fights the unread count.

              Just the number. It used to be an animated equaliser with the
              count beside it — two marks sharing about six pixels, one of them
              changing shape continuously. The bars still earn their keep at
              16px in the voice bar; in a rail chip they were a smudge. */}
          {voiceLabel !== null && (
            <Badge
              color="success"
              size="sm"
              placement="bottom-right"
              aria-label={t('ui.topbar.serverInVoice', { count: props.voiceCount ?? 0 })}
            >
              <span className="tabular-nums">{voiceLabel}</span>
            </Badge>
          )}
          {muted ? (
            <Badge color="default" size="sm" aria-label={t('ui.topbar.serverMuted')}>
              <BellOffIcon className="size-3" />
            </Badge>
          ) : (
            // Accent, not danger — unread is a queue, not a fault.
            railUnreadLabel(unread) && (
              <Badge color="accent" size="sm">
                {railUnreadLabel(unread)}
              </Badge>
            )
          )}
          </Badge.Anchor>
        </span>
        </button>
      </TooltipTarget>
      <Tooltip.Content showArrow placement={props.vertical ? 'right' : 'bottom'}>
        <Tooltip.Arrow />
        {tooltip}
      </Tooltip.Content>
    </Tooltip>
  );
}
